import React, { useEffect, useState } from 'react';
import { CardElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { createPaymentIntent, fetchBookingByPartnerId, orderBookingFinish, orderBookingFinishStatus, sendConfirmBookingEmail, sendDepositConfirmationEmail, updateRateHawkBookingStatus } from '../api-client';
import { loadStripe } from '@stripe/stripe-js';
import { useSearchContext } from '../contexts/SearchContext';
import PageLoaderComponent from './Common/PageLoaderSearch';
import { processBooking } from '../utils/bookingUtils';
import { useHotelContext } from '../contexts/HotelContext';
import { useAppContext } from '../contexts/AppContext';
import { useLocation } from 'react-router-dom';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface StripeProps {
  price: string;
  // reference: string;
  // bookingStatus: string;
  responseToken: string;
  //booking: BookingResponse;
  passengerEmail: string;
  formData: any;
}

interface CheckoutProps {
  price: string;
  // reference: string;
  // bookingStatus: string;
  responseToken: string;
  //booking: BookingResponse;
  passengerEmail: string;
  formData: any;
}

const EMAIL_RATEHAWK = import.meta.env.VITE_EMAIL_RATEHAWK || "";
const PHONE_RATEHAWK = import.meta.env.VITE_PHONE_RATEHAWK || "";
const FIRST_NAME_RATEHAWK = import.meta.env.VITE_FIRST_NAME_RATEHAWK || "";
const LAST_NAME_RATEHAWK = import.meta.env.VITE_LAST_NAME_RATEHAWK || "";

const CheckoutForm: React.FC<CheckoutProps & { onNext: () => void } & { onUpdate: (data: any) => void }> = ({ price, passengerEmail, onNext, formData, onUpdate }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { guests, checkIn, checkOut } = useSearchContext();
  const { bookingFormResponse } = useHotelContext();
  const { user } = useAppContext();
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(() => {
    return JSON.parse(localStorage.getItem('isPaymentSuccess') || 'false');
  });
  const [bookingCreated, setBookingCreated] = useState(false);
  const location = useLocation();
  const { bookingResponse } = location.state;
  const totalAdults = guests.reduce((sum, g) => sum + g.adults, 0);
  const totalChildren = guests.reduce((sum, g) => sum + g.children.length, 0);

  const totalCount = totalAdults + totalChildren;

  const [cardHolderName, setCardHolderName] = useState(() => {
    return localStorage.getItem('cardHolderName') || '';
  });

  useEffect(() => {
    const paymentStatus = JSON.parse(localStorage.getItem('isPaymentSuccess') || 'false');
    setIsPaymentSuccess(paymentStatus);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem('cardHolderName');
      localStorage.removeItem('email');
      localStorage.removeItem('phone');
      localStorage.removeItem('billingDetails');
      localStorage.removeItem('isPaymentSuccess');
    };

    // Attach the event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // Save the payment details to local storage whenever they change
    localStorage.setItem('cardHolderName', cardHolderName);
    localStorage.setItem('isPaymentSuccess', JSON.stringify(isPaymentSuccess));
  }, [cardHolderName,
    isPaymentSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage("Payment element is not mounted. Please refresh the page.");
      setIsProcessing(false);
      return;
    }
    //const detailsFromDb = await fetchBookingByReference(reference);

    const buildRoomsPayload = () => {
      const rooms: { guests: any[] }[] = [];
      let cursor = 0;

      guests.forEach(g => {
        const paxOfThisRoom = g.adults + g.children.length;
        const slice = formData.passengers.slice(cursor, cursor + paxOfThisRoom);

        rooms.push({
          guests: slice.map((p: { firstName: any; lastName: any; age: any; }) => ({
            first_name: p.firstName,
            last_name : p.lastName,
            age       : p.age
              ? Number(p.age)
              : null,
            is_child  : p.age ? true : null,
            gender    : null,
          })),
        });

        cursor += paxOfThisRoom;
      });

      return rooms;
    };


    try {
      const detailsFromDb = bookingResponse ? await fetchBookingByPartnerId(bookingResponse) : null;

      const amount = parseFloat(price);
      const totalAmount = amount * 100;
      const formattedTotalAmount = parseFloat(totalAmount.toFixed(2));

      const paidDeposit = detailsFromDb?.paidAmount || 0;

      const addAmount = paidDeposit === 0 ? amount : parseFloat(paidDeposit) + amount;

      const clientSecret = await createPaymentIntent(formattedTotalAmount, 'gbp', passengerEmail);

      const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement!,
          billing_details: {
            name: cardHolderName +"-"+ (detailsFromDb?.orderNumber ?? bookingFormResponse?.data.order_id.toString()),
          },
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        setIsPaymentSuccess(true);

        if (formData.hold === "paid-deposit" || formData.hold === "hold") {
          await updateRateHawkBookingStatus(bookingResponse, formData.selectedPlan === 'Easy Payment Plan' ? 'paid-deposit' : 'paid', addAmount.toFixed(2), new Date().toISOString());
        }

        const updatedDetailsFromDb = await fetchBookingByPartnerId(bookingResponse || ""); 

        if (formData.hold === "paid-deposit" || formData.hold === "hold") {
          if (formData.hold === "paid-deposit") {
            await sendConfirmBookingEmail(formData.email ?? formData.passengers[0].email, updatedDetailsFromDb!, 'paid');
          } else {
            if (formData.selectedPlan === 'Easy Payment Plan') {
              const totalDeposit = formData.selectedPrice * totalCount;
              await sendDepositConfirmationEmail(formData.email ?? formData.passengers[0].email, updatedDetailsFromDb!, 'paid', totalDeposit.toString());
            }
          }
          onUpdate({ isPaymentSuccess, bookingFromDB: updatedDetailsFromDb });
          onNext();
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 500));

        // console.log(bookingFormResponse.data);

        try {
          // Initial Finish Request
          const response = await orderBookingFinish({
            user: {
              email: EMAIL_RATEHAWK,
              comment: "",
              phone: PHONE_RATEHAWK,
            },
            supplier_data: {
              first_name_original: FIRST_NAME_RATEHAWK,
              last_name_original: LAST_NAME_RATEHAWK,
              phone: PHONE_RATEHAWK,
              email: EMAIL_RATEHAWK,
            },
            partner: {
              partner_order_id: bookingFormResponse.data.partner_order_id,
              comment: "",
              amount_sell_b2b2c: null,
            },
            language: "en",
            rooms: buildRoomsPayload(),
            upsell_data: [],
            payment_type: {
              type: bookingFormResponse.data.payment_types[0].type,
              amount: bookingFormResponse.data.payment_types[0].amount,
              currency_code: "GBP",
            },
          });
        
          if (response.status === "ok") {
            let statusResponse;
            let attempts = 0;
            const maxAttempts = 60; 
        
            do {
              await new Promise((resolve) => setTimeout(resolve, 2000)); // wait 1 second
        
              statusResponse = await orderBookingFinishStatus({ 
                partner_order_id: bookingFormResponse.data.partner_order_id
              });
        
              attempts++;
            } while (statusResponse.status !== "ok" && attempts < maxAttempts);
        
            if (statusResponse.status === "ok") {
              // Proceed to process booking
              await processBooking(
                user,
                formData.passengers[0].email ?? passengerEmail,
                addAmount,
                // formData.selectedPrice,
                // totalCount,
                bookingFormResponse,
                checkIn.toISOString(),
                checkOut.toISOString(),
                new Date().toISOString(),
                formData.book_hash,
                formData.selectedPlan === 'Easy Payment Plan' ? 'paid-deposit' : 'paid',
                formData.totalCost,
                'completed',
                bookingFormResponse.data.order_id.toString(),
                `${formData.passengers[0].firstName} ${formData.passengers[0].lastName}`,
                formData.passengers,
              );

              setBookingCreated(true);

              if(formData.selectedPlan === 'Easy Payment Plan' ) {
                const bookingDetails = await fetchBookingByPartnerId(bookingResponse ?? bookingFormResponse.data.partner_order_id);
                const totalDeposit = formData.selectedPrice * totalCount;
                await sendDepositConfirmationEmail(formData.passengers[0].email ?? passengerEmail, bookingDetails!, 'paid', totalDeposit.toString());
              } else if (formData.selectedPlan != 'Free Fare Hold') {
                const bookingDetails = await fetchBookingByPartnerId(bookingResponse ?? bookingFormResponse.data.partner_order_id);
                await sendConfirmBookingEmail(formData.passengers[0].email ?? passengerEmail, bookingDetails!, 'paid');
              }
            } else {
              console.error("Booking status did not return ok after multiple attempts.");
            }
          } else {
            console.error("Initial orderBookingFinish response not ok:", response);
          }
        } catch (error) {
          console.error("Error during order booking finish:", error);
        }
        
        
        //const detailsFromDb = await fetchBookingByReference(bookingDetails.reference);
        //const finalDetailsFromDb = await fetchBookingByReference(bookingDetails.reference);
        
        onUpdate({
          isPaymentSuccess,
          setBookingCreated: true,
          bookingCreated,
          // bookingResponse,
          // bookingDetails,
          // bookingFromDB: finalDetailsFromDb,
        });
        onNext();
      } 

      setErrorMessage(null);
    } catch (error: any) {
      console.error(error.message);
      setErrorMessage(error.message);
    }

    setIsProcessing(false);
  };

  return (
    <>
      {isProcessing && (
        <PageLoaderComponent />
      )}
      
      <form
        onSubmit={handleSubmit}
        className="payment-form max-w-md mx-auto p-6 bg-white shadow-md rounded-lg"
      >
        <div className="text-2xl font-bold text-gray-800 text-center">
          Payment Details
        </div>
        <hr className="mb-2 mt-2" />
        <div className="flex items-center justify-center gap-6">
          <img src="/featureIcons/visa.svg" alt="Visa" className="w-12 h-12" />
          <img src="/featureIcons/mastercard.svg" alt="Mastercard" className="w-12 h-12" />
          <img src="/featureIcons/amex.svg" alt="American Express" className="w-12 h-12" />
          <img src="/featureIcons/unionpay.svg" alt="Discover" className="w-12 h-12" />
        </div>
        <hr className="mb-2 mt-2" />

        {/* Cardholder's Name */}
        <div className="form-group mb-4">
          <label
            htmlFor="cardholder-name"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            CARDHOLDER NAME <span className="text-red-500">*</span>
          </label>
          <input
            id="cardholder-name"
            type="text"
            placeholder="Enter your name"
            value={cardHolderName}
            onChange={(e) => setCardHolderName(e.target.value)}
            required
            disabled={isPaymentSuccess}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
        </div>

        {/* Card Details */}
        <div className="form-group mb-4">
          <label
            htmlFor="card-element"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            CARD NUMBER <span className="text-red-500">*</span>
          </label>
          <div
            id="card-element"
            className="card-element-container px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
          >
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#32325d",
                    "::placeholder": {
                      color: "#a0aec0",
                    },
                  },
                  invalid: {
                    color: "#fa755a",
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="text-xl font-bold mb-6 text-gray-800 text-center">
          TOTAL AMOUNT: <span className='text-halo-pink'>£ {(parseFloat(price)).toFixed(2)}</span>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <p className="text-red-500 text-sm mb-4 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10A8 8 0 112 10a8 8 0 0116 0zm-9-3a1 1 0 112 0v3a1 1 0 11-2 0V7zM9 12a1 1 0 100 2h2a1 1 0 100-2H9z"
                clipRule="evenodd"
              />
            </svg>
            {errorMessage}
          </p>
        )}

        {/* Payment Button */}
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-300"
        >
          {isProcessing ? "Processing..." : "Pay Now"}
        </button>
      </form>
    </>
  );
};

const StripePayment: React.FC<StripeProps & { onNext: () => void } & { onUpdate: (data: any) => void }> = ({ price, passengerEmail, onNext, formData, onUpdate }) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm price={price} responseToken={formData.responseToken} passengerEmail={passengerEmail} onNext={onNext} formData={formData} onUpdate={onUpdate} />
  </Elements>
);

export default StripePayment;
